import React from "react"

import { Form, Formik, FormikHelpers } from "formik"
import * as Yup from "yup"

import dayjs from "src/dayjs"

import { gql, useMutation } from "hooks/urql"

import { DateObject } from "components/Forms/DatePicker"
import {
  DateField,
  TimeField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody, { Footer } from "components/ModalWithProvidedBody"

import type { AcrClassPeriod } from "./ClassPeriodsList"

type EditClassPeriodProps = {
  period: AcrClassPeriod
  timeZone: string
  ianaTimeZone: string
}

type EditClassPeriodFormState = {
  date: DateObject
  startsAt: DateObject
  endsAt: DateObject
}

const ValidationSchema = Yup.object().shape({
  date: Yup.mixed().required("Date is required"),
  startsAt: Yup.mixed().required("Start time is required"),
  endsAt: Yup.mixed().required("End time is required"),
})

const toDateObject = (timestamp: string, ianaTimeZone: string) =>
  new DateObject({
    date: dayjs(timestamp).tz(ianaTimeZone).format("YYYY-MM-DD HH:mm:ss"),
    format: "YYYY-MM-DD HH:mm:ss",
  })

const EditClassPeriod: React.FC<EditClassPeriodProps> = ({
  period,
  timeZone,
  ianaTimeZone,
}) => {
  const [, updateClassPeriod]: any[] = useMutation(updateClassPeriodMutation)

  const formState: EditClassPeriodFormState = {
    date: toDateObject(period.startsAt, ianaTimeZone),
    startsAt: toDateObject(period.startsAt, ianaTimeZone),
    endsAt: toDateObject(period.endsAt, ianaTimeZone),
  }

  const handleSubmit = (
    values: EditClassPeriodFormState,
    actions: FormikHelpers<EditClassPeriodFormState>,
    closeModal: VoidFunction
  ) => {
    updateClassPeriod({
      id: period.id,
      date: values.date.format("YYYY-MM-DD"),
      startTime: {
        hour: values.startsAt.hour,
        minute: values.startsAt.minute,
      },
      endTime: {
        hour: values.endsAt.hour,
        minute: values.endsAt.minute,
      },
    })
      .then((response: any) => {
        const failures = response.data?.updateAcrClassPeriod?.failures
        if (failures?.length > 0) {
          handleFailure(actions, failures)
        } else {
          window.location.reload()
        }
      })
      .catch(() => handleFailure(actions))
  }

  return (
    <ModalWithProvidedBody
      modalTitle="Edit Class Period"
      buttonText="Edit"
      buttonClassName="btn btn-info btn-sm"
    >
      {({ closeModal }: { closeModal: VoidFunction }) => (
        <Formik
          initialValues={formState}
          validationSchema={ValidationSchema}
          onSubmit={(values, actions) =>
            handleSubmit(values, actions, closeModal)
          }
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
        >
          <Form>
            <div className="modal-body">
              <p className="text-muted mb-3">
                Times are in teacher's timezone: {timeZone}
              </p>
              <DateField name="date" label="Date" />
              <TimeField name="startsAt" label="Start Time" />
              <TimeField name="endsAt" label="End Time" />
            </div>
            <Footer cancelAction={closeModal} submitText="Save" />
          </Form>
        </Formik>
      )}
    </ModalWithProvidedBody>
  )
}

const updateClassPeriodMutation = gql`
  mutation UpdateAcrClassPeriod(
    $id: ID!
    $date: Date!
    $startTime: TimeOfDayInputObject!
    $endTime: TimeOfDayInputObject!
  ) {
    updateAcrClassPeriod(
      id: $id
      date: $date
      startTime: $startTime
      endTime: $endTime
    ) {
      failures {
        message
      }
    }
  }
`

export default EditClassPeriod
